import { useMemo } from 'react';
import { useLocation } from 'react-router';

import { paths } from '@/app/router/paths';
import { useSessionStore } from '@/shared/api';

import { tourStepsFor } from './tour-steps';
import { useTourStore } from './tour-store';
import { TourOverlay } from './TourOverlay';
import { useCompleteTour } from './useCompleteTour';

/**
 * De rondleiding in de schil: vanzelf bij het eerste bezoek, of opnieuw vanuit Account.
 *
 * Vanzelf alleen op het dashboard - daar landt iedereen na het inloggen, en daar
 * staan alle plekken waar hij naar wijst. Wie via een link meteen in een opname
 * landt, krijgt hem dus pas als hij op het dashboard komt, niet midden in zijn werk.
 *
 * "Moet nog" is `user.tourCompletedAt === null` in de sessie (van de API). Een
 * sessie zonder het veld weet het niet, en dan komt hij niet.
 */
export function FirstRunTour() {
    const session = useSessionStore((state) => state.session);
    const impersonation = useSessionStore((state) => state.impersonation);
    const { pathname } = useLocation();
    const requested = useTourStore((state) => state.requested);
    const dismissedFor = useTourStore((state) => state.dismissedFor);
    const index = useTourStore((state) => state.index);
    const goTo = useTourStore((state) => state.goTo);
    const complete = useCompleteTour();

    const steps = useMemo(() => tourStepsFor(session, impersonation), [session, impersonation]);

    const owed = session?.user.tourCompletedAt === null && dismissedFor !== session.user.uuid;
    const onDashboard = pathname === paths.dashboard;

    if (steps.length === 0 || !onDashboard || !(requested || owed)) {
        return null;
    }

    const current = Math.min(index, steps.length - 1);

    return (
        <TourOverlay
            steps={steps}
            index={current}
            onBack={() => goTo(Math.max(0, current - 1))}
            onNext={() => goTo(Math.min(steps.length - 1, current + 1))}
            onClose={complete}
        />
    );
}
